import { FIELD_MODES, type LeadForm, type UpsertLeadForm } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Dialog, Field, Input, Select, Spinner, Switch } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Code2, Copy, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { fieldErrors } from '../clinical/patients-page.js';
import { api } from '../lib/api.js';
import { SectionHeader } from './layout.js';

const formsQuery = queryOptions({ queryKey: ['forms'], queryFn: () => api<LeadForm[]>('/forms') });
const EMPTY: UpsertLeadForm = {
  slug: 'avaliacao', name: 'Avaliação — site', title: 'Agende sua avaliação', subtitle: 'Deixe seu contato e a nossa equipe fala com você pelo WhatsApp.',
  fields: { email: 'optional', area: 'optional', message: 'off' }, successMessage: 'Recebemos! Em instantes falamos com você pelo WhatsApp.',
  welcomeWhatsapp: 'Olá, {nome}! Aqui é da {clinica}. Recebemos seu pedido de avaliação e já vamos te atender por aqui 😊', active: true,
};
const MODE_LABEL: Record<string, string> = { off: 'Não pedir', optional: 'Opcional', required: 'Obrigatório' };

function embedCode(url: string) {
  return `<div id="dnx-form"></div>
<script>(function(){var q=location.search.replace(/^\\?/,'');var f=document.createElement('iframe');
f.src='${url}?embed=1&page='+encodeURIComponent(location.href)+(q?'&'+q:'');
f.title='Formulário';f.style.cssText='width:100%;min-height:640px;border:0';
document.getElementById('dnx-form').appendChild(f);})();</script>`;
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return <Button size="sm" variant="secondary" onClick={() => { void navigator.clipboard.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }); }}><Copy className="size-3.5" />{done ? 'Copiado' : label}</Button>;
}

function Editor({ form, onClose }: { form: LeadForm | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<UpsertLeadForm>(form ? { slug: form.slug, name: form.name, title: form.title, subtitle: form.subtitle, fields: form.fields, successMessage: form.successMessage, welcomeWhatsapp: form.welcomeWhatsapp, active: form.active } : EMPTY);
  const [welcome, setWelcome] = useState(!!(form ? form.welcomeWhatsapp : EMPTY.welcomeWhatsapp));
  const save = useMutation({
    mutationFn: () => api<LeadForm>(form ? `/forms/${form.id}` : '/forms', { method: form ? 'PATCH' : 'POST', body: { ...f, welcomeWhatsapp: welcome ? f.welcomeWhatsapp : null } }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['forms'] }); onClose(); },
  });
  const errs = fieldErrors(save.error);
  const set = <K extends keyof UpsertLeadForm>(k: K, v: UpsertLeadForm[K]) => setF({ ...f, [k]: v });
  return (
    <Dialog open onClose={onClose} variant="drawer" title={form ? 'Editar formulário' : 'Novo formulário'}>
      <form className="grid gap-4 p-5" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome interno" error={errs.name}><Input value={f.name} onChange={(e) => set('name', e.target.value)} /></Field>
          <Field label="Endereço" hint="…/f/este-endereco" error={errs.slug}><Input value={f.slug} onChange={(e) => set('slug', e.target.value.toLowerCase())} /></Field>
        </div>
        <Field label="Título" error={errs.title}><Input value={f.title} onChange={(e) => set('title', e.target.value)} /></Field>
        <Field label="Subtítulo"><Input value={f.subtitle ?? ''} onChange={(e) => set('subtitle', e.target.value || null)} /></Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="E-mail"><Select value={f.fields.email} onChange={(e) => set('fields', { ...f.fields, email: e.target.value as typeof f.fields.email })}>
            {FIELD_MODES.map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
          </Select></Field>
          <Field label="Interesse"><Select value={f.fields.area} onChange={(e) => set('fields', { ...f.fields, area: e.target.value as 'off' | 'optional' })}>
            <option value="off">Não pedir</option><option value="optional">Opcional</option>
          </Select></Field>
          <Field label="Mensagem"><Select value={f.fields.message} onChange={(e) => set('fields', { ...f.fields, message: e.target.value as 'off' | 'optional' })}>
            <option value="off">Não pedir</option><option value="optional">Opcional</option>
          </Select></Field>
        </div>
        <Field label="Mensagem depois de enviar" error={errs.successMessage}><Input value={f.successMessage} onChange={(e) => set('successMessage', e.target.value)} /></Field>
        <Switch checked={welcome} onChange={setWelcome} label="Mandar boas-vindas no WhatsApp" description="Abre a conversa na hora; o assistente virtual segue o atendimento. Use {nome} e {clinica}." />
        {welcome && (
          <textarea aria-label="Mensagem de boas-vindas" value={f.welcomeWhatsapp ?? ''} onChange={(e) => set('welcomeWhatsapp', e.target.value)} rows={3} maxLength={1000}
            className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg focus:border-primary" />
        )}
        {form && <Switch checked={f.active} onChange={(v) => set('active', v)} label="Formulário ativo" />}
        {save.error && !Object.keys(errs).length && <Alert tone="danger">{save.error.message}</Alert>}
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button type="submit" loading={save.isPending}>Salvar</Button></div>
      </form>
    </Dialog>
  );
}

/** Configurações › Formulários: captura de leads do site/landing, com a origem (Google, Meta, site). */
export function FormsSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(formsQuery);
  const [editing, setEditing] = useState<LeadForm | 'new' | null>(null);
  const [code, setCode] = useState<LeadForm | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => api<void>(`/forms/${id}`, { method: 'DELETE' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['forms'] }) });
  return (
    <div className="grid gap-6">
      <SectionHeader title="Formulários de captura" description="Para o site e as landing pages da clínica. Guardam de onde a pessoa veio (Google, Meta, campanha) e já criam o lead com a origem certa."
        actions={<Button onClick={() => setEditing('new')}><Plus className="size-4" />Novo formulário</Button>} />
      {remove.error && <Alert tone="danger">{remove.error.message}</Alert>}
      {isLoading ? <Spinner className="size-5 text-fg-subtle" label="Carregando" /> : (
        <Card className="divide-y divide-border overflow-hidden">
          {data?.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 font-medium text-fg">{f.name}{!f.active && <Badge>Inativo</Badge>}</p>
                <p className="truncate font-mono text-[12.5px] text-fg-muted">{f.url}</p>
                <p className="text-[12.5px] text-fg-muted">{f.submissions} envio(s){f.lastSubmissionAt ? ` · último ${new Date(f.lastSubmissionAt).toLocaleDateString('pt-BR')}` : ''}</p>
              </div>
              <a href={f.url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="size-4" />Abrir</Button></a>
              <Button size="sm" variant="secondary" onClick={() => setCode(f)}><Code2 className="size-4" />Código para o site</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(f)}><Pencil className="size-4" />Editar</Button>
              {f.submissions === 0 && <Button size="sm" variant="ghost" className="text-danger" onClick={() => remove.mutate(f.id)}><Trash2 className="size-4" /></Button>}
            </div>
          ))}
          {data && !data.length && <p className="px-5 py-8 text-center text-sm text-fg-muted">Nenhum formulário. Crie o de avaliação e cole no site ou use o link direto nos anúncios.</p>}
        </Card>
      )}
      {editing && <Editor form={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      <Dialog open={!!code} onClose={() => setCode(null)} title="Código para o site" description="Cole onde o formulário deve aparecer. Ele repassa a origem da visita (UTM, gclid, fbclid).">
        {code && (
          <div className="grid gap-3 p-5">
            <pre className="overflow-x-auto rounded-lg bg-surface-muted p-3 font-mono text-[12px] whitespace-pre-wrap break-all text-fg">{embedCode(code.url)}</pre>
            <div className="flex flex-wrap gap-2"><CopyButton text={embedCode(code.url)} label="Copiar código" /><CopyButton text={code.url} label="Copiar link direto" /></div>
            <p className="text-[12.5px] text-fg-muted">No Google Ads, ative a marcação automática (gclid). Nos anúncios da Meta, o fbclid já vem no link.</p>
          </div>
        )}
      </Dialog>
    </div>
  );
}
